import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { searchUsers } from "@/lib/api";
import { formatTimestamp } from "@/lib/utils";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import "./admin.css";

const message = (error: unknown) => error instanceof Error ? error.message : String(error);

const shortDate = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const dateOf = (value?: string | null) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? shortDate.format(date) : "—";
};

/** Search legacy PlayFab accounts. The query lives in the URL so returning from an account restores the results. */
export function PlayFabLegacyWorkspace() {
  const readOnly = usePreviewReadOnly();
  const [params, setParams] = useSearchParams();
  const q = params.get("q")?.trim() ?? "";
  const [value, setValue] = useState(q);
  useEffect(() => { setValue(q); }, [q]);
  const search = useQuery({ queryKey: ["playfab-search", q], queryFn: ({ signal }) => searchUsers(q, signal), enabled: !!q, staleTime: 60_000, retry: false });

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = value.trim();
    if (!next) return;
    if (next === q) void search.refetch();
    else setParams({ q: next });
  }

  const rows = search.data?.results ?? [];
  return <WorkspaceShell crumbs={[{ label: "Administration" }, { label: "PlayFab (legacy)" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page">
      <div className="ws-head"><h1>PlayFab (legacy)</h1></div>
      <form onSubmit={submit} className="ws-card ws-form">
        <div className="ad-add">
          <input aria-label="Search legacy accounts" value={value} onChange={e => setValue(e.target.value)} placeholder="UID, display name, username, account or Google email" autoFocus />
          <button type="submit" className="pw-button pw-button-primary" disabled={!value.trim() || search.isFetching}>
            <Search size={14} />{search.isFetching ? "Searching…" : "Lookup"}</button>
        </div>
        <p className="ad-meta ws-muted">Display names are case-sensitive; emails are not. Google email only matches accounts with recorded legacy metadata — otherwise look up by UID.</p>
      </form>
      {search.error && <p className="ws-alert" role="alert">{message(search.error)}</p>}
      {search.data?.warnings.map(warning => <p key={warning} className="ws-alert is-warn">{warning}</p>)}
      {q && <section className="ws-card is-flush ad-card-flush">
        <div className="ws-card-head"><h2>Results</h2>{search.data && <span className="ws-muted">{rows.length} account{rows.length === 1 ? "" : "s"}</span>}</div>
        {search.isPending ? <div className="ws-empty" role="status"><span className="pw-loading-dot" /> Searching PlayFab…</div>
          : rows.length === 0 ? <p className="ws-empty">{search.error ? "Search failed." : search.data?.warnings.length ? `No accounts returned for “${q}”. Search coverage is incomplete; see the warnings above.` : `No legacy accounts match “${q}”.`}</p>
          : <div className="ws-table ad-table" role="table" aria-label="Legacy PlayFab accounts">
            <div className="ws-table-row ws-table-head ad-legacy-row" role="row">{["Player", "Username", "Email", "Google email", "Created", "Last login"].map(h => <span role="columnheader" key={h}>{h}</span>)}</div>
            {rows.map(row => {
              const name = row.TitleInfo?.DisplayName;
              // Accounts PlayFab never reported a creation date for fall back to when our backend first saw them.
              const created = row.TitleInfo?.Created ?? row.Created ?? row.BackendFirstSeen;
              const createdTitle = row.TitleInfo?.Created ?? row.Created ? formatTimestamp(created) : created ? `First seen on backend ${formatTimestamp(created)}` : undefined;
              const lastLogin = row.TitleInfo?.LastLogin;
              return <Link key={row.PlayFabId} className="ws-table-row ad-legacy-row ad-legacy-result" role="row"
                to={`/players/${encodeURIComponent(row.PlayFabId)}`} state={{ origin: "playfab" }} aria-label={`Open legacy account ${row.PlayFabId}`}>
                <span role="cell" className="ad-main"><span className="admin-account-link">{name || row.PlayFabId}</span>
                  {name && <span className="ad-sub">{row.PlayFabId}</span>}</span>
                <span className="ad-break" aria-hidden="true" />
                <span role="cell" className={row.Username ? undefined : "ad-muted ad-blank"} data-label="user">{row.Username ?? "—"}</span>
                <span role="cell" className={row.PrivateInfo?.Email ? "ad-muted" : "ad-muted ad-blank"} data-label="email" title={row.PrivateInfo?.Email}>{row.PrivateInfo?.Email ?? "—"}</span>
                <span role="cell" className={row.GoogleInfo?.GoogleEmail ? "ad-muted" : "ad-muted ad-blank"} data-label="google" title={row.GoogleInfo?.GoogleEmail}>{row.GoogleInfo?.GoogleEmail ?? "—"}</span>
                <span role="cell" className="ad-muted" data-label="created" title={createdTitle}>{dateOf(created)}</span>
                <span role="cell" className="ad-muted" data-label="last login" title={lastLogin ? formatTimestamp(lastLogin) : undefined}>{dateOf(lastLogin)}</span>
              </Link>;
            })}
          </div>}
      </section>}
    </div></div>
  </WorkspaceShell>;
}
